import React, { useEffect, useState, useRef } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import {
  InputField,
  AgGridDataTable,
  TonnageDisclaimer,
} from "../../shared";
import { Form, Button, Col, Alert } from "react-bootstrap";
import { callWasteCodeMatrixService } from "../../../services/WasteCodeMatrixService";
import urlParameterHelper from "../../../utilities/urlParameters";

const schema = yup.object({
  entityType: yup.string(),
  epaId: yup.string(),
  wasteCodeType: yup.string(),
});

const WasteCodeMatrixGrid = (props) => {
  const agGridRef = useRef();
  const formikRef = useRef();
  const submitRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [hasError, setHasError] = useState({ message: "", error: false });
  const [columnDefs, setColumnDefs] = useState([{ headerName: "", field: "" }]);
  const initialFormValues = {
    epaId: props.epaId,
    entityType: "generator",
    wasteCodeType: "ca",
  };

  const formConfig = [
    {
      name: "entityType",
      config: {
        label: "Entity Type",
        col: "md",
        colWidth: "3",
        placeholder: "",
        type: "select",
        data: [
          {
            label: "Alt. TSDF",
            value: "AlternateDesignateFacility",
          },
          { label: "Generator", value: "generator" },
          { label: "Transporter", value: "transporter" },
          { label: "TSDF", value: "tsdf" },
        ],
      },
    },
    {
      name: "wasteCodeType",
      config: {
        label: "Waste Code Type",
        col: "md",
        colWidth: "3",
        placeholder: "",
        type: "select",
        data: [
          { label: "Non-RCRA", value: "ca" },
          { label: "RCRA", value: "rcra" },
        ],
      },
    },
  ];

  const onSubmit = async (data, formikBag) => {
    search(data, formikBag);
  };

  const search = async (formData, formikBag) => {
    let obj = {};

    Object.keys(formData).forEach((key) => {
      if (formData[key] !== "") {
        obj[key] = formData[key];
      }
    });

    const qs = urlParameterHelper.stringify(obj);
    agGridRef.current.api.showLoadingOverlay();

    await callWasteCodeMatrixService(qs)
      .then((res) => {
        if (res.data.length === 0) {
          setColumnDefs([{ headerName: "", field: "" }]);
          setRowData([]);

          agGridRef.current.api.showNoRowsOverlay();
        } else {
          setAgGrid(res.data);
          agGridRef.current.api.hideOverlay();
        }

        var allColumnIds = [];
        agGridRef.current.columnApi.getAllColumns().forEach((column) => {
          allColumnIds.push(column.colId);
        });
        agGridRef.current.columnApi.autoSizeColumns(allColumnIds, false);
        agGridRef.current.api.sizeColumnsToFit();
        agGridRef.current.columnApi.autoSizeAllColumns();

        setHasError({ error: false, message: "" });
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          let requiredFields = {};

          Object.keys(err.response.data).forEach((key, i) => {
            requiredFields = {
              ...requiredFields,
              [key]: err.response.data[key][0],
            };
          });

          formikBag.setErrors(requiredFields);
        }

        if (err.response && err.response.status === 500) {
          setHasError({
            error: true,
            message: "An error has occured. Please try again.",
          });
        }
        setColumnDefs([{ headerName: "", field: "" }]);
        setRowData([]);
        agGridRef.current && agGridRef.current.api.showNoRowsOverlay();
      });

    formikBag.setSubmitting(false);
  };

  const setAgGrid = (data) => {
    /*
      Columns start out like this:
        2024
        2025
        Waste Code
        Description
      
      The UI wants them like this
        Waste Code
        Description
        2025
        2024
    */
    const exampleRow = data[0];
    const dataColumns = Object
      .keys(exampleRow) // 2024, 2025, Waste_Code, Description
      .sort()           // 2024, 2025, Description, Waste_Code
      .reverse();       // Waste_Code, Description, 2025, 2024
    const agGridColumns = dataColumns.map((col) => {
      return {
        headerName: col.replace(/_/g, " "),
        field: col,
        sortable: true,
      };
    });
    setColumnDefs(agGridColumns);
    setRowData(data);
  };

  useEffect(() => {
    formikRef.current.setFieldValue("epaId", props.epaId);
    setTimeout(function () {
      formikRef.current.submitForm();
    }, 1000);
  }, [props.epaId]);

  return (
    <div>
      <Formik
        innerRef={formikRef}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={schema}
        onSubmit={(data, { setSubmitting, resetForm, setErrors }) => {
          setSubmitting(true);
          onSubmit(data, { setSubmitting, resetForm, setErrors });
        }}
        initialValues={initialFormValues}
      >
        {({
          isSubmitting,
          handleReset,
          handleSubmit,
          handleChange,
          values,
          touched,
          errors,
          resetForm,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Form.Row>
              {formConfig.map((input, i) => (
                <InputField
                  initialFormValues={initialFormValues[input.name]}
                  // setFieldValue={setFieldValue}
                  key={i}
                  name={input.name}
                  config={input.config}
                  values={values}
                  handleChange={handleChange}
                  errors={errors}
                  touched={touched}
                />
              ))}
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} md="1">
                <Button ref={submitRef} type="submit" disabled={isSubmitting}>
                  Search
                </Button>
              </Form.Group>
              <Form.Group as={Col} md="1">
                <Button
                  onClick={resetForm}
                  type="reset"
                  disabled={isSubmitting}
                >
                  Reset
                </Button>
              </Form.Group>
            </Form.Row>
            {hasError.error && (
              <Alert variant="danger">{hasError.message}</Alert>
            )}
          </Form>
        )}
      </Formik>
      <AgGridDataTable
        pageTitle={"Waste Code By Year Matrix Report"}
        formParams={[]}
        agGridRef={agGridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        initialPageSize={50}
        hideRecordCnt={true}
      />
      <TonnageDisclaimer />
    </div>
  );
};

// const TonnageReportAnt = Form.create()(TonnageReport);

export default WasteCodeMatrixGrid;
